<!-- eslint-disable vue/no-restricted-html-elements -->
<template>
  <template v-if="zoom && !isMobile">
    <photoswipe-lightbox
      ref="photoswipe"
      :active-index="false"
      :child-ref="`media-${product.handle}-${activeCode}`"
      :media="media"
      :namespace="namespace"
      :product="product"
    />
  </template>

  <splide
    v-else
    ref="splide"
    class="product-carousel"
    :class="`product-carousel--${namespace}`"
    :has-track="false"
    :options="localConfig"
    :aria-label="$string('product.gallery.label', { replace: { product: product.title } })"
    @splide:mounted="handleSplideMounted"
    @splide:moved="handleSplideMoved"
  >
    <splide-track>
      <splide-slide
        v-for="(mediaItem, index) of media"
        :key="`media-${product.handle}-${activeCode}-${index}`"
        :ref="`media-${product.handle}-${activeCode}-${index}`"
        :data-index="index"
      >
        <div
          v-if="type !== 'thumbnails' && mediaItem.mediaType === 'video'"
          class="product-carousel__media-container"
          :class="{
            'product-carousel__media-container--modal': type === 'modal',
            [$class('loading')]: loading,
          }"
        >
          <video-player
            class="product-carousel__video"
            :display-overlay="false"
            :is-active="getVideoActive(mediaItem.index)"
            :video="getVideoSource(mediaItem.sources)"
          />
        </div>

        <component
          :is="type === 'thumbnails' ? 'button' : 'div'"
          v-else
          class="product-carousel__image-container"
          :class="{
            [$class('loading')]: loading,
            [$class('active')]: type === 'thumbnails' && index === currentIndex
          }"
          @keydown.enter.prevent="handleImageClick(index)"
        >
          <responsive-image
            class="product-carousel__image"
            :class="
              isThumbnailVideo(mediaItem.mediaType)
                ? 'product-carousel__video-thumbnail'
                : ''
            "
            :image="mediaItem.previewImage || mediaItem.image || mediaItem"
            :lazyload="type === 'thumbnails' ? index > 5 : index > 0"
            :min-max="minMax"
            :ratio="ratio"
            :sizes="sizes"
            tabindex="-1"
            :alt="product.title + ' view ' + (index+1)"
            @click.prevent="handleImageClick(index)"
          />

          <icon-play
            v-if="isThumbnailVideo(mediaItem.mediaType)"
            class="product-carousel__play-icon"
          />
        </component>
      </splide-slide>
    </splide-track>

    <div
      v-if="config.arrows"
      class="product-carousel__arrows splide__arrows"
    >
      <button
        class="splide__arrow splide__arrow--prev"
        :disabled="disablePrevArrow"
        type="button"
      >
        <span
          class="visually-hidden"
          v-text="$string('accessibility.carousel.previous')"
        />

        <icon-chevron-left-two />
      </button>

      <button
        class="splide__arrow splide__arrow--next"
        :disabled="disableNextArrow"
        type="button"
      >
        <span
          class="visually-hidden"
          v-text="$string('accessibility.carousel.next')"
        />

        <icon-chevron-right-two />
      </button>
    </div>
  </splide>
</template>

<script>

/**
 * Vue: Product carousel (product-carousel)
 * -----------------------------------------------------------------------------
 * Splide powered carousel shared between gallery and modal.
 *
 * @param {String} activeCode - Active color code.
 * @param {Object} config - Splide carousel config.
 * @param {Number} currentIndex - Current carousel index.
 * @param {String} currentNamespace - Current carousel namesoace.
 * @param {String} [layout] - Current layout.
 * @param {Boolean} [loading] - Loading state.
 * @param {Array} media - Media array.
 * @param {String} minMax - Min max attribute.
 * @param {String} namespace - Namespace, either `gallery` or `modal`.
 * @param {Object} product - Local product object.
 * @param {String} ratio - Ratio attribute.
 * @param {String} sizes - Sizes attribute.
 * @param {String} type - Type of carousel, `main`, `thumbnails`, or `modal`.
 *
 * @emits splideMounted - Emit Splide mounted event.
 * @emits updateCurrentIndex -  Emit update current carousel index event.
 *
 */
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'
import { mapActions, mapGetters, mapState } from 'vuex'
import { setIntersectionObserver } from '~/helpers/general'

import PhotoswipeLightbox from '~async/photoswipe-lightbox/photoswipe-lightbox'
import VideoPlayer from '~async/video-player/video-player'
import ResponsiveImage from '~global/images/responsive-image'

import IconChevronLeftTwo from '~icons/directional-navigation/chevron/left-2.svg'
import IconChevronRightTwo from '~icons/directional-navigation/chevron/right-2.svg'
import IconPlay from '~icons/general/play.svg'

export default {
  name: 'ProductCarousel',

  components: {
    IconChevronLeftTwo,
    IconChevronRightTwo,
    IconPlay,
    PhotoswipeLightbox,
    ResponsiveImage,
    Splide,
    SplideSlide,
    SplideTrack,
    VideoPlayer,
  },

  props: {
    activeCode: {
      type: String,
      default: '',
    },
    config: {
      type: Object,
      default: () => ({}),
    },
    currentIndex: {
      type: Number,
      default: 0,
    },
    currentNamespace: {
      type: String,
      default: 'gallery',
    },
    layout: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    media: {
      type: Array,
      default: () => [],
      required: true,
    },
    minMax: {
      type: String,
      default: '300-1000',
    },
    namespace: {
      type: String,
      default: 'gallery',
    },
    product: {
      type: [Boolean, Object],
      default: () => ({
        handle: '',
        images: [],
        media: [],
        title: '',
      }),
    },
    ratio: {
      type: String,
      default: '20:23',
    },
    sizes: {
      type: String,
      default: '100vw',
    },
    type: {
      type: String,
      default: 'main',
    },
    zoom: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'splide-mounted': () => true,
    'update-current-index': (payload) => typeof payload === 'number',
  },

  data() {
    return {
      isSmoothScroll: false,
      localConfig: {},
      localSlideIndex: 0,
      observers: [],
      skipUpdateCurrentIndex: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getRtlWritingDirection: 'getRtlWritingDirection',
    }),

    /**
     * Determine whether to disable the next arrow button.
     * @returns {Boolean}
     */
    disableNextArrow() {
      return this.media.length <= this.localSlideIndex + 1
    },

    /**
     * Determine whether to disable the previous arrow.
     * @returns {Boolean}
     */
    disablePrevArrow() {
      return this.localSlideIndex <= 0
    },

    /**
     * Compute if current gallery namespace is active.
     * @returns {Boolean}
     */
    namespaceActive() {
      return this.currentNamespace === this.namespace
    },
  },

  watch: {

    /**
     * Watch active code to re-create intersection observers.
     */
    activeCode() {
      this.setCarouselIntersectionObserver()
    },

    /**
     * Watch media changes to ensure event listeners work for video files
     * added to the media array after mounting.
     */
    media() {
      this.setCarouselIntersectionObserver()
    },

    /**
     * Watch current media index to update slide index whilst active.
     * - This is used to show variant media.
     */
    currentIndex() {
      if (
        !this.namespaceActive ||
        this.currentIndex === this.localSlideIndex
      ) {
        return
      }

      if (this.layout === 'grid' && this.isSmoothScroll) {
        this.smoothScrollToIndex()
        return
      }

      this.resetSettings()
      this.goToIndex(this.currentIndex, true)
    },

    /**
     * Watch is mobile to force refresh the carousel.
     * - Fixes issue where switching from grid to other layout broke the arrows
     *   and pagination.
     */
    async isMobile() {
      if (
        this.namespace !== 'gallery' ||
        !this.isMobile ||
        this.layout === 'grid'
      ) {
        return
      }

      await this.$nextTick()
      this.goToIndex(0)
    },

    /**
     * Watch namespace active to update visible slide.
     */
    async namespaceActive() {
      if (!this.namespaceActive) {
        return
      }

      await this.goToIndex(this.currentIndex)
    },

    /**
     * Watch product to refresh gallery position.
     */
    async product() {
      if (!this.namespaceActive) {
        return
      }

      await this.goToIndex(this.currentIndex)
    },
  },

  beforeMount() {
    this.localConfig = {
      direction: this.getRtlWritingDirection ? 'rtl' : 'ltr',
      drag: true,
      flickPower: 200,
      flickMaxPages: 1,
      mediaQuery: 'min',
      pagination: false,
      speed: this.$timing('normal'),
      start: this.currentIndex || 0,
      type: 'slide',
      ...this.config,
    }

    /**
     * Hide arrows in modal gallery when only one item.
     */
    if (this.media.length !== 1) {
      return
    }

    this.localConfig.arrows = false
  },

  mounted() {
    this.setEventBusListeners()
    this.setRatioVariable()
  },

  beforeUnmount() {
    if (this.observers.length > 0) {
      this.observers.forEach((observer) => observer.disconnect())
    }
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    /**
     * Handle Splide mounted event.
     * @param {Object} splide - Splide instance.
     */
    handleSplideMounted() {
      this.$emit('splide-mounted')
    },

    /**
     * Handle Splide moved event.
     * - Updates current slide index to share with modal.
     * @param {Object} splide - Splide instance.
     * @param {Number} newIndex - New slide index.
     * @param {Number} destIndex - Destination slide index.
     */
    handleSplideMoved(_, newIndex) {
      this.localSlideIndex = newIndex

      if (newIndex === this.currentIndex) {
        return
      }

      this.$emit('update-current-index', newIndex)
    },

    /**
     * Go to slide.
     * - Set speed to 0 to instantly update slide without animation.
     * @param {Number} index - Slide index to go to.
     * @param {Boolean} animate - Animate slide transition.
     * @returns {Promise}
     */
    goToIndex(index, animate = false) {
      return new Promise((resolve) => {
        if (!this.$refs.splide) {
          resolve()
          return
        }

        if (!animate) {
          this.localConfig.speed = 0
        }

        setTimeout(() => {
          this.$refs.splide.go(index)
        }, 0)

        if (!animate) {
          setTimeout(() => {
            this.localConfig.speed = this.config.speed || this.$timing('normal')
          }, 10)
        }

        resolve()
      })
    },

    /**
     * Get video active state.
     * - Play video when gallery is active and slide is visible.
     * @param {Number} index - Video media index.
     * @returns {Boolean}
     */
    getVideoActive(index) {
      return this.layout === 'grid'
        ? this.namespaceActive
        : this.namespaceActive && index === this.currentIndex
    },

    /**
     * Get video MP4 source object.
     * @param {Array} sources - Video sources.
     * @returns {Object}
     */
    getVideoSource(sources) {
      return sources.find((source) => source.format === 'mp4')
    },

    /**
     * Return if the current thumbnail is for a video.
     * @param {String} mediaType - Media type.
     * @returns {Boolean}
     */
    isThumbnailVideo(mediaType) {
      return this.type === 'thumbnails' && mediaType === 'video'
    },

    /**
     * Set aspect ratio variable for formatting video wrapper.
     * Prevents mobile carousel from stretching container when video is tall.
     */
    setRatioVariable() {
      document.documentElement.style.setProperty(
        '--product-carousel-ratio',
        `${this.ratio.replace(':', '/')}`,
      )
    },

    /**
     * Handle image click.
     * @param {Number} index - Clicked media index.
     */
    handleImageClick(index) {
      if (this.type === 'modal') {
        return
      }

      this.$emit('update-current-index', index)

      if (this.type === 'thumbnails') {

        /**
         * Send cnvs event to prevent intersection observer from interrupting
         * index.
         */
        cnvs.EventBus.emit('is-thumbnail-click', true)
        return
      }

      this.openOverlay({
        component: 'product-gallery-modal',
        ignoreDismissed: true,
        namespace: 'productGalleryModal',
      })
    },

    /**
     * Set intersection observer for grid images.
     */
    async setCarouselIntersectionObserver() {
      if (this.type !== 'main') {
        return
      }

      await this.$nextTick().then(() => {
        const imageRefsArray = this.media.map((_, index) => {
          return `media-${this.product.handle}-${this.activeCode}-${index}`
        })

        imageRefsArray.forEach((imageRef) => {
          const refElement = this.getRefElement(imageRef)

          if (!refElement) {
            return
          }

          this.setCarouselImageIntersectionObserver(refElement)
        })
      })
    },

    /**
     * Helper function to call setIntersectionObserver.
     */
    setCarouselImageIntersectionObserver(element) {
      if (!element) {
        return
      }

      this.$nextTick().then(() => {
        this.observers.push(
          setIntersectionObserver({
            callback: this.handleComponentIntersection,
            element,
            rootMargin: '0px',
            threshold: 0.65,
          }),
        )
      })
    },

    /**
     * Handle component intersection.
     * @param {Array} entries - Intersection entries.
     */
    handleComponentIntersection(entries) {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return
        }

        cnvs.EventBus.listen('is-thumbnail-click', (result) => {
          this.skipUpdateCurrentIndex = result
        })

        if (this.skipUpdateCurrentIndex) {
          return
        }

        const newIndex = Number(entry.target.dataset.index)

        this.$emit('update-current-index', newIndex)
      })
    },

    /**
     * Set Canvas event bus listeners.
     */
    setEventBusListeners() {
      cnvs.EventBus.listen('is-thumbnail-click', (result) => {
        this.isSmoothScroll = result
      })
    },

    /**
     * Get $refs element based on param.
     * @param {String} imageRef - Image ref string.
     * @returns {HTMLElement}
     */
    getRefElement(imageRef) {
      return this.zoom && !this.isMobile
        ? this.$refs.photoswipe.$refs[imageRef][0]
        : this.$refs[imageRef][0].$el
    },

    /**
     * Handle smooth scrolling to target image when clicking on a thumbnail
     * image.
     */
    async smoothScrollToIndex() {
      await this.$nextTick()
        .then(() => {

          /**
           * Ensure that the current index exists.
           */
          const targetIndex = this.currentIndex >= this.media.length
            ? this.media.length - this.currentIndex
            : this.currentIndex

          const targetImageRefs = `media-${this.product.handle}-${this.activeCode}-${targetIndex}`

          const refElement = this.getRefElement(targetImageRefs)

          if (!refElement) {
            return
          }

          refElement.scrollIntoView(
            {
              behaviour: 'smooth',
              block: 'center',
            },
          )

          /**
           * Use setTimeout because there's no native event when scrollIntoView
           * finishes.
           */
          setTimeout(() => {
            this.resetSettings()
          }, this.$timing('slower'))
        })
    },

    /**
     * Reset settings to default.
     */
    resetSettings() {
      this.localSlideIndex = this.currentIndex
      this.isSmoothScroll = false
      this.skipUpdateCurrentIndex = false
    },
  },
}
</script>

<style lang="scss">
@import './product-carousel';
</style>
